import { useProfileMenuOptions } from "@circle-react-shared/uikit/HeaderV4/ProfileDropdown/useProfileMenuOptions";
import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useIsCommunityAdmin } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";
import { Menu } from "@circleco/compass/components/Menu";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Link, useRouterState } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { buildSettingsNavItems } from "@/react/components/CircleAi/Dock/buildSettingsNavItems";
import {
  DOCK_NAV_ACTIVE_CLASSES,
  DOCK_NAV_ICON_BUTTON_CLASSES,
} from "@/react/components/CircleAi/Dock/constants";
import { useIsAdminDockNavVisible } from "@/react/components/CircleAi/Dock/useIsAdminDockNavVisible";
import { useSettingsNavLinks } from "@/react/components/CircleAi/Dock/useSettingsNavLinks";
import { CirclePartnerBadgeCollapsedIcon } from "@/react/components/Layout/CirclePartnerBadge/CirclePartnerBadgeCollapsedIcon";
import { useIsAdminArea } from "@/react/components/SettingsApp/CompassCompat/useIsAdminArea";

import { ProfileMenuTrigger } from "./ProfileMenuTrigger";

const SETTINGS_PATH = "/settings";

const SettingsButton = ({ isActive }: { isActive: boolean }) => (
  <Tooltip content={t("admin_nav_v4.settings")} side="right">
    <Link
      to={SETTINGS_PATH}
      aria-label={t("admin_nav_v4.settings")}
      className={classNames(
        DOCK_NAV_ICON_BUTTON_CLASSES,
        isActive && DOCK_NAV_ACTIVE_CLASSES,
      )}
    >
      <CompassIcon
        name={isActive ? "settings-gear-filled" : "settings-gear"}
        size="md"
        color="primary"
      />
    </Link>
  </Tooltip>
);

export const DockFooterContent = () => {
  const isAdminArea = useIsAdminArea();
  const isAdminDockNavVisible = useIsAdminDockNavVisible();
  const isAdmin = useIsCommunityAdmin();
  const { currentCommunityMember } = usePunditUserContext();
  const dockMode = useCircleAIShellStore(s => s.dockMode);
  const { menuOptions, isDisabled } = useProfileMenuOptions();
  const pathname = useRouterState({ select: s => s.location.pathname });

  const settingsNavLinks = useSettingsNavLinks();
  const settingsGroups = buildSettingsNavItems(settingsNavLinks);
  const settingsPaths = settingsGroups.flatMap(group =>
    (group.children ?? []).map(child => child.to),
  );

  if (!isAdminArea && !isAdminDockNavVisible) return null;

  const isSettingsActive =
    pathname === SETTINGS_PATH ||
    settingsPaths.some(p => pathname.startsWith(p));
  const name = currentCommunityMember?.name ?? "";
  const avatarUrl = currentCommunityMember?.avatar_url ?? undefined;
  const shouldShowSettingsButton = isAdmin;

  const profileMenu = (showName: boolean) => (
    <Menu
      align="end"
      side="right"
      options={menuOptions}
      disabled={isDisabled}
      trigger={
        <ProfileMenuTrigger
          avatarUrl={avatarUrl}
          name={name}
          showName={showName}
        />
      }
      data-testid="dock-user-profile-menu"
    />
  );

  if (dockMode === "collapsed") {
    return (
      <div className="flex flex-col items-center gap-3 p-3">
        <CirclePartnerBadgeCollapsedIcon />
        {shouldShowSettingsButton && (
          <SettingsButton isActive={isSettingsActive} />
        )}
        <CircleAIAppShell.DockToggleButton />
        {profileMenu(false)}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 p-3">
      {profileMenu(true)}
      <div className="flex items-center gap-1">
        {shouldShowSettingsButton && (
          <SettingsButton isActive={isSettingsActive} />
        )}
        <CircleAIAppShell.DockToggleButton />
      </div>
    </div>
  );
};
